<template>
  <div>
    <b-card no-body class="card-custom" header-tag="header" footer-tag="footer">
      <template v-slot:header>
        <div class="card-title">
          <h3 class="card-label">
            {{ $t("MENU.ITEM.EXP.AI_INVOICE_ACCOUNTING_VALUE_TYPES") }}
            <i class="mr-2"></i>
            <small class="">{{ $t("LABELS.LIST") }}</small>
          </h3>
        </div>
        <div class="card-toolbar">
          <router-link
            v-permission="['ai-invoice-accounting-value-types-create']"
            to="add"
            class="btn btn-light-success font-weight-bolder"
            ><i class="flaticon2-plus"></i>
            {{ $t("LABELS.ADD_NEW") }}</router-link
          >
        </div>
      </template>
      <v-card v-show="!isPageLoading">
        <SearchTools
          :searchQuery.sync="searchQuery"
          @do-search="doSearch"
          @do-clear="doClear"
        >
        </SearchTools>
        <v-data-table
          :headers="tableHeaders"
          :items="items"
          :options.sync="options"
          :items-per-page="totalItems"
          :server-items-length="totalItems"
          :loading="isLoadingAiInvoiceAccountingValueType"
          loading-text="Loading... Please wait"
          :footer-props="{
            showFirstLastPage: true,
            itemsPerPageOptions: rowsPerPageItems,
            firstIcon: 'mdi-arrow-collapse-left',
            lastIcon: 'mdi-arrow-collapse-right'
          }"
          @update:options="getDataFromApi()"
          class="px-6 elevation-1"
        >
          <template v-slot:[`item.aiInvoiceTypeName`]="{ item }">
            <span v-if="item.invoiceTypes && item.invoiceTypes.length">
              <span
                v-for="type in item.invoiceTypes"
                :key="type.id"
                class="invoice-type-badge"
              >
                {{ type.name }}
              </span>
            </span>
            <span v-else>-</span>
          </template>
          <template v-slot:[`item.actions`]="{ item }">
            <router-link
              v-if="
                checkIfUserHasPermission([
                  'ai-invoice-accounting-value-types-update'
                ])
              "
              :to="{
                name: 'edit-ai-invoice-accounting-value-types',
                params: { id: item.id }
              }"
            >
              <v-tooltip right>
                <template v-slot:activator="{ on, attrs }">
                  <v-btn class="mr-2" fab dark x-small v-bind="attrs" v-on="on">
                    <v-icon dark>mdi-pencil</v-icon>
                  </v-btn>
                </template>
                <span>{{ $t("LABELS.EDIT") }}</span>
              </v-tooltip>
            </router-link>
            <template
              v-if="
                !checkIfUserHasPermission([
                  'ai-invoice-accounting-value-types-update'
                ])
              "
              >No permissions</template
            >
          </template>
          <template v-slot:no-results>
            <v-alert :value="true" color="error" icon="warning">
              Your search for "{{ searchQuery }}" found no results.
            </v-alert>
          </template>
        </v-data-table>
      </v-card>

      <v-skeleton-loader
        v-if="isPageLoading"
        :loading="isPageLoading"
        type="table"
      ></v-skeleton-loader>
    </b-card>
  </div>
</template>

<script>
import { SET_BREADCRUMB } from "@/core/services/store/breadcrumbs.module";
import { mapGetters } from "vuex";
import SearchTools from "@/core/components/table/SearchTools.vue";
import { FETCH_AI_INVOICE_ACCOUNTING_VALUE_TYPES } from "@/modules/ai-Invoice-accounting-value-types/store/ai-invoice-accounting-value-types.module";

import { permissionMixin } from "@/core/mixins/permissionMixin.js";

export default {
  mixins: [permissionMixin],
  data() {
    return {
      isPageLoading: true,
      tableLoading: true,
      items: [],
      totalItems: 100,
      searchQuery: "",
      options: {
        descending: true,
        page: 1,
        itemsPerPage: 30,
        totalItems: 100
      },
      rowsPerPageItems: [10, 20, 30, 40, 50],
      columns: {},
      cancelSource: null,
      headers: [
        { text: this.$i18n.t("FORM_LABELS.NAME"), value: "name" },
        { text: this.$i18n.t("FORM_LABELS.CODE"), value: "code" },
        {
          text: this.$i18n.t("MENU.ITEM.EXP.AI_INVOICE_TYPE"),
          value: "aiInvoiceTypeName",
          sortable: false
        },
        {
          align: "right",
          text: "Actions",
          value: "actions",
          sortable: false,
          width: "10%"
        }
      ]
    };
  },
  components: { SearchTools },
  watch: {},
  mounted() {
    this.$store.dispatch(SET_BREADCRUMB, [
      {
        title: this.$i18n.t("MENU.ITEM.EXP.AI_INVOICE_ACCOUNTING_VALUE_TYPES"),
        route: { name: "list-ai-invoice-accounting-value-types" }
      }
    ]);
  },
  computed: {
    ...mapGetters(["isLoadingAiInvoiceAccountingValueType"]),
    params() {
      return {
        ...this.options,
        query: this.searchQuery
      };
    },
    tableHeaders() {
      return this.headers;
    }
  },
  methods: {
    cancelRequest() {
      if (this.cancelSource) {
        this.cancelSource.cancel("Start new search, stop active search");
      }
    },
    doSearch() {
      let vm = this;
      if (vm.options.page == 1) {
        vm.getDataFromApi();
      } else {
        vm.options.page = 1;
      }
    },
    doClear() {
      let vm = this;
      vm.searchQuery = "";
      if (vm.options.page == 1) {
        vm.getDataFromApi();
      } else {
        vm.options.page = 1;
      }
    },
    getDataFromApi() {
      let vm = this;
      let params = this.params;

      params.length = params.itemsPerPage;
      params.start =
        params.page == 1 ? 0 : params.itemsPerPage * (params.page - 1);

      let apiParams = vm.transformUrlParams(params);

      vm.$store
        .dispatch(FETCH_AI_INVOICE_ACCOUNTING_VALUE_TYPES, apiParams)
        .then(data => {
          vm.$nextTick(function() {
            vm.items = data.data.items;
            vm.totalItems = data.data.totalItemsCount;

            if (vm.isPageLoading == true) {
              vm.isPageLoading = false;
            }
          });
        })
        .catch(response => {
          if (response.status === 404) {
            vm.$notify({
              group: "notify",
              type: "error",
              text: "Not Found"
            });
            this.$router.push({
              name: "list-ai-invoice-accounting-value-types"
            });
          }
        });
    },
    transformUrlParams(params) {
      let apiParams =
        "?" +
        Object.keys(params)
          .map(key => key + "=" + params[key])
          .join("&");
      return apiParams;
    }
  }
};
</script>

<style scoped>
.invoice-type-badge {
  display: inline-block;
  background: #e3f2fd;
  color: #1976d2;
  border-radius: 12px;
  padding: 2px 10px;
  margin: 0 3px 3px 0;
  font-size: 12px;
  font-weight: 500;
  vertical-align: middle;
}
</style>
